import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import { EstimateMeterReadResponse } from "@/api/backofficeRest/generated/model"
import {
  IMeterReadEstimate,
  IRegisterEstimate,
  RegisterWrapKind,
} from "./meterReadEstimate.types"

export type IMeterReadEstimateRaw = EstimateMeterReadResponse

type IRegisterEstimateRaw = NonNullable<IMeterReadEstimateRaw["registers"][0]>

/**
 * Anything the backend has not told us about is treated as "not wrapped", so
 * an unrecognised value can never quietly suppress the read. A value we do not
 * know is a contract change, not a rollover.
 */
const toWrapKind = (raw?: string | null): RegisterWrapKind | null => {
  const known = Object.values(RegisterWrapKind) as string[]

  return raw && known.includes(raw) ? (raw as RegisterWrapKind) : null
}

const toRegisterEstimate = (raw: IRegisterEstimateRaw): IRegisterEstimate => ({
  registerName: raw.registerName,
  estimatedRead: raw.estimatedRead,
  estimatedReadFormatted: raw.estimatedReadFormatted,
  wrapped: toWrapKind(raw.wrapped),
  estimatedConsumptionKwh: raw.estimatedConsumptionKwh,
  targetPeriodWeight: raw.targetPeriodWeight,
  knownPeriodWeight: raw.knownPeriodWeight ?? null,
  estimatedAqKwh: raw.estimatedAqKwh ?? null,
})

export const MeterReadEstimateParser: IParser<
  IMeterReadEstimate,
  IMeterReadEstimateRaw
> = {
  toDomain: (raw: IMeterReadEstimateRaw) => {
    try {
      return {
        seasonal: raw.seasonal,
        registers: (raw.registers ?? []).map(toRegisterEstimate),
        validationErrors: raw.validationErrors ?? [],
        calorificValueApplied: raw.calorificValueApplied ?? null,
      }
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
